import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  AskResponse,
  AssistantIntent,
  ChatMessage,
  HealthResponse,
  IndexStatus,
} from './types';

export const aiAssistantApiRef = createApiRef<AiAssistantApi>({
  id: 'plugin.ai-assistant.service',
});

export interface AiAssistantApi {
  ask(question: string, history: ChatMessage[], intent?: AssistantIntent): Promise<AskResponse>;
  reindex(): Promise<{ status: string; stats: unknown }>;
  getIndexStatus(): Promise<IndexStatus>;
  getHealth(): Promise<HealthResponse>;
}

export class AiAssistantClient implements AiAssistantApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('ai-assistant');
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${base}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      },
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        message = body.error?.message || body.error || body.message || message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return response.json();
  }

  ask(question: string, history: ChatMessage[], intent: AssistantIntent = 'ask'): Promise<AskResponse> {
    return this.request('/ask', {
      method: 'POST',
      body: JSON.stringify({ question, history, mode: 'rag', intent }),
    });
  }

  reindex(): Promise<{ status: string; stats: unknown }> {
    return this.request('/index', { method: 'POST', body: '{}' });
  }

  getIndexStatus(): Promise<IndexStatus> {
    return this.request('/index/status');
  }

  getHealth(): Promise<HealthResponse> {
    return this.request('/health');
  }
}
